import { useState, useContext } from 'react';
import { LocalizationContext } from 'src/Context/LocalizationContext';
import { errorHandler } from 'src/Config/ErrorHandler';
import { Attachments, host } from 'src/Branding/config';
import { DataContext } from 'src/Context/DataContext';
import { RFIAttachment } from '@refineintel/types/RFITypes';

export interface CreateAttachmentResponse {
	attachmentData: RFIAttachment;
	attachmentId: string;
	status: string;
	version: string;
}

export const useQueryCreateAttachment = (): {
	isLoading: boolean;
	createAttachment: (data: File, parentType?: string, parentId?: string) => Promise<CreateAttachmentResponse | null>;
	errorMessage: string | null;
	resetErrorMessage: () => void;
} => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const { sc: shortCode } = useContext(DataContext);
	const { FileUpload, LanguageCode } = useContext(LocalizationContext);
	const { UploadFilesUploadErrorMessage: UploadError, UploadFilesMaxSizeExceededError: MaxSizeExceededError } =
		FileUpload;
	const { UploadFilesUnsupportedFormatErrorMessage } = FileUpload;

	const resetErrorMessage = () => {
		setErrorMessage(null);
	};

	const createAttachment = async (
		data: File,
		parentType?: string,
		parentId?: string,
	): Promise<CreateAttachmentResponse | null> => {
		const extensionError = !Attachments.SupportedExtensions.includes(
			data.name.toLowerCase()?.split('.').pop() as string,
		);
		const sizeError = data.size > Attachments.AttachmentSizeLimit;
		if (sizeError) {
			setErrorMessage(MaxSizeExceededError);
			return null;
		}
		if (extensionError) {
			setErrorMessage(UploadFilesUnsupportedFormatErrorMessage);
			return null;
		}

		setIsLoading(true);

		const url = new URL(host + '/api/createAttachment');
		const formData = new FormData();
		formData.append('parentType', parentType || '');
		formData.append('parentId', parentId || '');
		formData.append('files', data);
		formData.append('sc', shortCode ?? '');
		formData.append('lang', LanguageCode ?? 'en-US');
		const requestOptions = {
			method: 'POST',
			withCredentials: true,
			body: formData,
		};

		try {
			const res = await fetch(url, requestOptions);
			const resData: CreateAttachmentResponse = await res.json();

			setIsLoading(false);

			if (resData.status === 'error') {
				setErrorMessage(UploadError);
				return null;
			}

			return resData;
		} catch (err) {
			errorHandler(new Error('Error creating attachment'), { err });
			throw new Error(err as any);
		} finally {
			setIsLoading(false);
		}
	};

	return { isLoading, createAttachment, errorMessage, resetErrorMessage };
};
